import * as React from 'react';
import classNames from 'classnames';
import * as Classes from '../common/classes';
import { useMemo } from 'react';
import { isFloat, isInt, unifyStringValueAsNumber } from './NumberUtils';
import {
  FormattedInput,
  IFormattedInputBaseProps,
  ValueAndCursor,
} from '../formatted-input/FormattedInput';
import { InputAddons } from '../input-addons/InputAddons';
import { IAddonsProps, IInputSearchProps } from '../common/props';

const parserNumber = (value: string | undefined, decimal?: boolean) => {
  if (value == undefined) {
    return undefined;
  }

  // Odebrání mezer, převedení desetinné čárky na tečku
  let s = unifyStringValueAsNumber(value);

  if (value == '') {
    return undefined;
  }

  let x;
  try {
    if (!!decimal) {
      x = parseFloat(s);
    } else {
      x = parseInt(s);
    }

    if (isNaN(x)) {
      return undefined;
    } else {
      return x;
    }
  } catch (e) {
    return undefined;
  }
};

// Odebrání mezer, převedení desetinné tečky na čárku apod.
const unifyEditedValue = (valueAndCursor: ValueAndCursor): ValueAndCursor => {
  const text = valueAndCursor.value;
  if (!text) {
    return { value: text };
  }

  let cursorIndex = valueAndCursor.cursorIndex || 0;
  let outputText = '';
  for (let i = 0; i < text.length; i++) {
    let c = `${text.charAt(i)}`;
    c = c
      .trim()
      // @ts-ignore
      .replaceAll(/\+/g, '')
      .replaceAll(/\s?/g, '')
      .replaceAll(/[.]/g, ',');
    if (cursorIndex > i && c.length === 0) {
      cursorIndex--;
    }
    outputText += c;
  }

  if (outputText === '-0') {
    if (cursorIndex > 0) {
      cursorIndex--;
    }
    outputText = '0';
  }

  return { value: outputText, cursorIndex: cursorIndex };
};

export interface INumberInputProps
  extends IFormattedInputBaseProps<number>,
    IInputSearchProps,
    IAddonsProps {
  /**
   * Pokud je true, tak input nebude oddělovat tisíce.
   */
  noThousandsSeparator?: boolean;
  /**
   * Pokud je true, lze zadávat pouze kladná čísla a 0.
   */
  onlyPositive?: boolean;
  /**
   * Pokud je true, je možné zadat desetinná čísla.
   */
  decimal?: boolean;
  /**
   * Na kolik desetinných míst - max., výchozí hodnota je 2.
   */
  maximumFractionDigits?: number;
  autoFocus?: boolean;
}

export type NumberInputProps = INumberInputProps;

/**
 * Komponenta pro editaci celého nebo reálného čísla.
 */
export const NumberInput: React.FC<NumberInputProps> = ({
  className,
  decimal,
  maximumFractionDigits = 2,
  onlyPositive,
  noThousandsSeparator,
  addonAfter,
  addonAfterCompact,
  onSearch,
  disabled,
  readOnly,
  dataTest,
  autoFocus,
  ...other
}) => {
  const maxDigits: number | undefined = 16;

  const formatterViewFloat = useMemo(() => {
    return new Intl.NumberFormat('cs-CZ', {
      maximumFractionDigits:
        maximumFractionDigits === 0 ? undefined : maximumFractionDigits,
      useGrouping: !noThousandsSeparator,
    });
  }, [maximumFractionDigits]);

  const formatterViewInt = useMemo(() => {
    return new Intl.NumberFormat('cs-CZ', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
      useGrouping: !noThousandsSeparator,
    });
  }, []);

  return (
    <InputAddons addonAfter={addonAfter} addonAfterCompact={addonAfterCompact}>
      <FormattedInput<number>
        dataTest={dataTest}
        onSearch={onSearch}
        className={classNames(className, Classes.NUMBER_INPUT, {
          [Classes.FIELD_INPUT_DISABLED]: disabled,
          [Classes.FIELD_INPUT_READONLY]: readOnly,
        })}
        {...other}
        disabled={disabled}
        readOnly={readOnly}
        isValidInput={(inputValue) =>
          (decimal &&
            isFloat(inputValue, !onlyPositive, undefined, undefined)) ||
          (!decimal && isInt(inputValue, !onlyPositive, undefined))
        }
        isValidValue={(inputValue) =>
          (decimal &&
            isFloat(
              inputValue,
              !onlyPositive,
              maxDigits,
              maximumFractionDigits
            )) ||
          (!decimal && isInt(inputValue, !onlyPositive, maxDigits))
        }
        formatViewValue={(value) =>
          decimal
            ? formatterViewFloat.format(value)
            : formatterViewInt.format(value)
        }
        unifyEditedValue={unifyEditedValue}
        parseValue={(inputValue) => parserNumber(inputValue, decimal)}
        autoFocus={autoFocus}
      />
    </InputAddons>
  );
};
